import { Link } from 'react-router';
import { useNavContext, type NavContext } from './navContext';

export interface Crumb {
  to: string;
  label: string;
}

/**
 * Bildet den Pfad Start › Organisation › Basar › Bereich aus der Adresse, dem Kontext der Basar-Hülle und dem
 * Zurück-Ziel der Seite. Jede Seite gibt nur ihr direktes Elternglied (`back`) an, die übrigen Glieder ergeben
 * sich aus dem Kontext. Fehlt der Kontext (noch nicht geladen), bleibt es bei Start › Elternglied.
 */
export function buildTrail(pathname: string, back: Crumb, context: NavContext): Crumb[] {
  const trail: Crumb[] = [{ to: '/', label: 'Start' }];
  const push = (crumb: Crumb) => {
    if (trail.some((entry) => entry.to === crumb.to)) return;
    trail.push(crumb);
  };
  const eventMatch = /^\/basare\/([^/]+)/.exec(pathname);
  const eventId = eventMatch?.[1];
  if (eventId !== undefined && context.eventId === eventId) {
    if (context.orgId !== null && context.orgName !== null) {
      push({ to: `/organisationen/${context.orgId}`, label: context.orgName });
    }
    if (context.eventTitle !== null && pathname.replace(/\/$/, '') !== `/basare/${eventId}`) {
      push({ to: `/basare/${eventId}`, label: context.eventTitle });
    }
  }
  push(back);
  return trail;
}

/**
 * Nur die Vorfahren: Das Elternglied (`back`) fehlt, weil es der Zurück-Knopf im Seitenkopf ist (Plan 16, U19b).
 */
export function buildAncestors(pathname: string, back: Crumb, context: NavContext): Crumb[] {
  return buildTrail(pathname, back, context).filter((crumb) => crumb.to !== back.to);
}

/**
 * Brotkrumen-Leiste. Alle Vorfahren sind Links, die aktuelle Seite steht als letzter Eintrag
 * (`aria-current="page"`). Am Handy entfällt die Leiste, dort bleibt der Zurück-Knopf im Seitenkopf.
 */
export function Breadcrumbs({
  trail,
  current,
}: {
  trail: Crumb[];
  /** Titel der aktuellen Seite. */
  current: string;
}) {
  return (
    <nav className="crumbs" aria-label="Brotkrumen">
      <ol className="crumbs__list">
        {trail.map((crumb) => (
          <li key={crumb.to} className="crumbs__item">
            <Link to={crumb.to}>{crumb.label}</Link>
          </li>
        ))}
        <li className="crumbs__item crumbs__item--current">
          <span aria-current="page">{current}</span>
        </li>
      </ol>
    </nav>
  );
}

/** Brotkrumen einer Seite mit automatisch ergänztem Kontext (Organisation, Basar). */
export function PageCrumbs({
  back,
  current,
  pathname,
}: {
  back: Crumb;
  current: string;
  pathname: string;
}) {
  const context = useNavContext();
  const trail = buildAncestors(pathname, back, context);
  return <Breadcrumbs trail={trail} current={current} />;
}
